/**
 * ArTableShell
 *
 * Unified outer shell for ArTable. Owns the `ArTableUIState` proxy and the
 * row + schema subscriptions via `StoreProvider`.
 *
 * The proxy lives in `formStore.state.tables` when the table is inside a
 * form (top-level), so out-of-tree consumers on the form page can read
 * selection / pagination / filters via `formStore.getTableSlot(parentPath, tableId)`.
 * Otherwise (entity routed page, nested entity in form, modal-loaded entity)
 * it lives per-instance via `useRef`.
 */

import { FC, useContext, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import ArTableBody from './components/ArTableBody';
import { StoreProvider, formStore, PageScopeContext } from '../../store';
import { MessageContext } from '../../providers/MessageContext';
import { ArTableProvider, ArTableViewSortContext } from './context';
import { useArTablePagination } from './pagination';
import { usePdfExportMode } from './usePdfExportMode';
import { resolveArTableRowsOrderBy } from './resolveArTableRowsOrderBy';
import { createArTableState, type ArTableUIState } from './state/createArTableState';
import type { ArTableViewSort } from '#types/System.Artifacts.types';
import { arTableRowsPath } from '#types/System.Artifacts.types';
import type { ArTableShellProps } from './ArTableShell.types';

const ArTableShell: FC<ArTableShellProps> = ({
    tableId,
    parentPath,
    disabled = false,
    stickyFirstColumn,
    visibilityMode,
    columnConfig,
}) => {
    const { messageApi } = useContext(MessageContext);
    const pageScope = useSnapshot(useContext(PageScopeContext));
    const formSnap = useSnapshot(formStore.state);
    const insideFormContext = formSnap.formId !== undefined;
    const insideEntityRecordContext = pageScope.source === 'preview'
        ? pageScope.preview.insideEntityRecordContext
        : pageScope.source === 'entity' && pageScope.entityId !== '';
    const useFormStoreSlot = insideFormContext && !insideEntityRecordContext;

    const storeKey = `${parentPath}:${tableId}`;
    const schemaPath = `tableTypes/${tableId}`;
    const rowsPath = arTableRowsPath(parentPath, tableId);
    const [viewSort, setViewSort] = useState<ArTableViewSort>({ mode: 'order' });
    const viewSortContextValue = useMemo(() => ({ viewSort, setViewSort }), [viewSort]);

    const instanceUIStateRef = useRef<ArTableUIState | null>(null);
    if (instanceUIStateRef.current === null) {
        instanceUIStateRef.current = createArTableState();
    }

    const uiState = useMemo(
        () => (useFormStoreSlot
            ? formStore.registerTable(parentPath, tableId).ui
            : instanceUIStateRef.current!),
        [useFormStoreSlot, parentPath, tableId],
    );

    const rowsOrderBy = useMemo(() => resolveArTableRowsOrderBy(viewSort), [viewSort]);

    const { paginationConstraints } = useArTablePagination({
        uiState,
        rowsOrderBy,
    });

    /**
     * PDF export must show every row (and every selected row across pages),
     * so we drop the page-size limit from the rows query. The pagination UI
     * is hidden in PDF mode (`showInteractiveControls` in ArTableBody), so
     * the cursor work that `useArTablePagination` performs is unused.
     */
    const isPdfExportMode = usePdfExportMode();
    const effectiveRowConstraints = useMemo(
        () => (isPdfExportMode ? [] : paginationConstraints),
        [isPdfExportMode, paginationConstraints],
    );

    const stores = useMemo(() => ({
        schema: {
            type: 'document' as const,
            path: schemaPath,
            onError: () => {
                messageApi.error('Unable to load table schema. Please refresh the page.');
            },
        },
        rows: {
            type: 'collection' as const,
            path: rowsPath,
            queryOptions: {
                orderBy: rowsOrderBy,
                extraConstraints: effectiveRowConstraints,
            },
            onError: () => {
                messageApi.error('Unable to load table data. Please refresh the page.');
            },
        },
    }), [messageApi, rowsOrderBy, rowsPath, schemaPath, effectiveRowConstraints]);

    return (
        <StoreProvider
            key={storeKey}
            stores={stores}
        >
            <ArTableViewSortContext.Provider value={viewSortContextValue}>
                <ArTableProvider uiState={uiState} disabled={disabled}>
                    <ArTableBody
                        tableId={tableId}
                        parentPath={parentPath}
                        disabled={disabled}
                        stickyFirstColumn={stickyFirstColumn}
                        visibilityMode={visibilityMode}
                        columnConfig={columnConfig}
                    />
                </ArTableProvider>
            </ArTableViewSortContext.Provider>
        </StoreProvider>
    );
};

export default ArTableShell;
